/* ==========================================================================
   Life in 3D – RNR Redesign — global.js
   Custom elements: drawers, quantity inputs, product forms (AJAX cart),
   announcement rotation, reveal-on-scroll, predictive search.
   ========================================================================== */

(function () {
  'use strict';

  const trapStack = [];

  function getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
  }

  function trapFocus(container, initialFocus) {
    const handler = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = getFocusable(container);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    trapStack.push({ container, handler, previouslyFocused: document.activeElement });
    (initialFocus || getFocusable(container)[0] || container).focus();
  }

  function releaseFocus(container) {
    const idx = trapStack.findIndex((t) => t.container === container);
    if (idx === -1) return;
    const { handler, previouslyFocused } = trapStack[idx];
    document.removeEventListener('keydown', handler);
    trapStack.splice(idx, 1);
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') previouslyFocused.focus();
  }

  function formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const withThousands = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const fmt = format || window.theme.moneyFormat || '${{amount}}';
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, `${withThousands}.${dec}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withThousands)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}${dec ? ',' + dec : ''}`);
  }

  window.theme = window.theme || {};
  window.theme.formatMoney = formatMoney;

  /* ------------------------------------------------------------------ */
  /* Drawer base                                                         */
  /* ------------------------------------------------------------------ */

  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    open() {
      if (this.hasAttribute('data-disabled')) return;
      this.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      trapFocus(this.querySelector('.drawer__panel') || this.querySelector('.menu-drawer__panel') || this);
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      document.body.style.overflow = '';
      releaseFocus(this.querySelector('.drawer__panel') || this.querySelector('.menu-drawer__panel') || this);
    }
  }

  customElements.define('cart-drawer', class extends DrawerElement {});
  customElements.define('search-drawer', class extends DrawerElement {
    open() {
      super.open();
      const input = this.querySelector('input[type="search"]');
      if (input) setTimeout(() => input.focus(), 50);
    }
  });
  customElements.define('menu-drawer', class extends DrawerElement {});
  customElements.define('facet-drawer', class extends DrawerElement {});

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const target = document.getElementById(opener.getAttribute('data-drawer-open'));
    if (target && typeof target.open === 'function') {
      event.preventDefault();
      target.open();
    }
  });

  /* ------------------------------------------------------------------ */
  /* Quantity input                                                      */
  /* ------------------------------------------------------------------ */

  customElements.define(
    'quantity-input',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.addEventListener('click', (e) => {
          const button = e.target.closest('button');
          if (!button) return;
          e.preventDefault();
          const previous = parseInt(this.input.value, 10) || 0;
          const min = parseInt(this.input.min, 10) || 0;
          let next = button.name === 'plus' ? previous + 1 : previous - 1;
          if (next < min) next = min;
          if (next !== previous) {
            this.input.value = next;
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    }
  );

  /* ------------------------------------------------------------------ */
  /* Cart helpers                                                        */
  /* ------------------------------------------------------------------ */

  async function refreshCartDrawer(openAfter) {
    const drawer = document.getElementById('CartDrawer');
    const response = await fetch(`${window.theme.routes.cart_url}?view=drawer-items`);
    const html = await response.text();
    const itemsContainer = drawer && drawer.querySelector('[data-cart-drawer-items]');
    if (itemsContainer) itemsContainer.innerHTML = html;

    const cart = await (await fetch(`${window.theme.routes.cart_url}.js`)).json();
    updateCartUI(cart, drawer);

    if (openAfter && drawer && typeof drawer.open === 'function') drawer.open();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    return cart;
  }

  function updateCartUI(cart, drawer) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count > 0 ? cart.item_count : '';
    });
    if (!drawer) drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    const count = drawer.querySelector('[data-cart-drawer-count]');
    if (count) count.textContent = `(${cart.item_count})`;
    const subtotal = drawer.querySelector('[data-cart-drawer-subtotal]');
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    const footer = drawer.querySelector('[data-cart-drawer-footer]');
    if (footer) footer.hidden = cart.item_count === 0;
    const checkoutBtn = footer && footer.querySelector('[name="checkout"]');
    if (checkoutBtn) checkoutBtn.disabled = cart.item_count === 0;

    const bar = drawer.querySelector('[data-free-shipping-bar]');
    if (bar) {
      const threshold = parseInt(bar.dataset.threshold, 10);
      const progressEl = bar.querySelector('[data-free-shipping-progress]');
      const messageEl = bar.querySelector('[data-free-shipping-message]');
      const progress = Math.min((cart.total_price / threshold) * 100, 100);
      if (progressEl) progressEl.style.width = `${progress}%`;
      bar.classList.toggle('free-shipping-bar--reached', cart.total_price >= threshold);
      if (messageEl) {
        if (cart.item_count === 0 && bar.dataset.i18nEmpty) {
          messageEl.textContent = bar.dataset.i18nEmpty;
        } else if (cart.total_price >= threshold && bar.dataset.i18nReached) {
          messageEl.textContent = bar.dataset.i18nReached;
        } else if (bar.dataset.i18nRemaining) {
          messageEl.textContent = bar.dataset.i18nRemaining.replace('[amount]', formatMoney(threshold - cart.total_price));
        }
      }
    }
  }

  window.theme.refreshCartDrawer = refreshCartDrawer;

  /* Line item changes (drawer + cart page) */
  document.addEventListener('change', async (event) => {
    const input = event.target.closest('.quantity__input[data-line]');
    if (!input) return;
    await changeLine(parseInt(input.dataset.line, 10), parseInt(input.value, 10) || 0, input);
  });

  document.addEventListener('click', async (event) => {
    const removeBtn = event.target.closest('[data-cart-remove]');
    if (!removeBtn) return;
    event.preventDefault();
    await changeLine(parseInt(removeBtn.dataset.line, 10), 0, removeBtn);
  });

  async function changeLine(line, quantity, sourceEl) {
    try {
      const response = await fetch(window.theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      if (!response.ok) throw new Error('cart change failed');
      const onCartPage = document.querySelector('[data-cart-page]');
      if (onCartPage) {
        window.location.reload();
      } else {
        await refreshCartDrawer(false);
      }
    } catch (err) {
      if (sourceEl) sourceEl.setCustomValidity && sourceEl.setCustomValidity(window.theme.strings.cartError);
      window.location.href = window.theme.routes.cart_url;
    }
  }

  /* Cart note */
  document.addEventListener('change', (event) => {
    const note = event.target.closest('[data-cart-note]');
    if (!note) return;
    fetch(window.theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note.value }),
    });
  });

  /* ------------------------------------------------------------------ */
  /* Product form (AJAX add to cart)                                     */
  /* ------------------------------------------------------------------ */

  customElements.define(
    'product-form',
    class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.submitButton = this.form.querySelector('[type="submit"]');
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.submitButton.classList.add('is-loading');
        this.submitButton.setAttribute('aria-busy', 'true');

        const formData = new FormData(this.form);
        try {
          const response = await fetch(`${window.theme.routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
          });
          const data = await response.json();
          if (!response.ok) {
            this.showError(data.description || window.theme.strings.cartError);
            return;
          }
          const drawerDisabled = document.getElementById('CartDrawer')?.hasAttribute('data-disabled');
          if (drawerDisabled) {
            window.location.href = window.theme.routes.cart_url;
          } else {
            await refreshCartDrawer(true);
          }
        } catch (err) {
          this.showError(window.theme.strings.cartError);
        } finally {
          this.submitButton.classList.remove('is-loading');
          this.submitButton.removeAttribute('aria-busy');
        }
      }

      showError(message) {
        let errorEl = this.querySelector('.product-form__error');
        if (!errorEl) {
          errorEl = document.createElement('div');
          errorEl.className = 'form__message form__message--error product-form__error';
          errorEl.setAttribute('role', 'alert');
          this.form.insertAdjacentElement('afterend', errorEl);
        }
        errorEl.textContent = message;
      }
    }
  );

  /* ------------------------------------------------------------------ */
  /* Announcement bar rotation                                           */
  /* ------------------------------------------------------------------ */

  customElements.define(
    'announcement-bar',
    class extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('.announcement-bar__slide'));
        if (this.slides.length < 2) return;
        this.setAttribute('data-rotating', '');
        this.index = 0;
        this.slides[0].classList.add('is-active');
        const interval = parseInt(this.dataset.interval, 10) || 5000;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this.timer = setInterval(() => {
          this.slides[this.index].classList.remove('is-active');
          this.index = (this.index + 1) % this.slides.length;
          this.slides[this.index].classList.add('is-active');
        }, interval);
      }
      disconnectedCallback() {
        clearInterval(this.timer);
      }
    }
  );

  /* ------------------------------------------------------------------ */
  /* Reveal on scroll                                                    */
  /* ------------------------------------------------------------------ */

  function initReveal() {
    const items = document.querySelectorAll('.reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    items.forEach((el) => observer.observe(el));
  }

  document.addEventListener('DOMContentLoaded', initReveal);
  document.addEventListener('shopify:section:load', initReveal);

  /* ------------------------------------------------------------------ */
  /* Predictive search                                                   */
  /* ------------------------------------------------------------------ */

  customElements.define(
    'predictive-search',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.results = this.querySelector('#PredictiveResults');
        if (!this.input || !this.results) return;
        this.input.addEventListener('input', this.debounce(this.onInput.bind(this), 250));
      }

      debounce(fn, wait) {
        let t;
        return (...args) => {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(this, args), wait);
        };
      }

      async onInput() {
        const query = this.input.value.trim();
        if (query.length < 2) {
          this.results.innerHTML = '';
          this.input.setAttribute('aria-expanded', 'false');
          return;
        }
        try {
          const url = `${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article,query&resources[limit]=6&section_id=predictive-search`;
          const response = await fetch(url);
          if (!response.ok) return;
          const text = await response.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const inner = doc.querySelector('#shopify-section-predictive-search');
          if (inner) {
            this.results.innerHTML = inner.innerHTML;
            this.input.setAttribute('aria-expanded', 'true');
          }
        } catch (err) {
          /* silent — fall through to full search page on submit */
        }
      }
    }
  );

  /* ------------------------------------------------------------------ */
  /* Sticky add-to-cart visibility (product page)                        */
  /* ------------------------------------------------------------------ */

  function initStickyAtc() {
    const sticky = document.querySelector('[data-sticky-atc]');
    const anchor = document.querySelector('[data-atc-anchor]');
    if (!sticky || !anchor || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
        if (visible) sticky.removeAttribute('inert');
        else sticky.setAttribute('inert', '');
      },
      { threshold: 0 }
    );
    observer.observe(anchor);
  }

  document.addEventListener('DOMContentLoaded', initStickyAtc);

  /* ------------------------------------------------------------------ */
  /* Modal helpers (size guide, zoom)                                    */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (opener) {
      const modal = document.getElementById(opener.getAttribute('data-modal-open'));
      if (modal) {
        event.preventDefault();
        modal.setAttribute('open', '');
        document.body.style.overflow = 'hidden';
        trapFocus(modal.querySelector('.modal__content') || modal);
      }
      return;
    }
    const closer = event.target.closest('[data-modal-close]');
    if (closer) {
      const modal = closer.closest('.modal');
      if (modal) {
        modal.removeAttribute('open');
        document.body.style.overflow = '';
        releaseFocus(modal.querySelector('.modal__content') || modal);
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openModal = document.querySelector('.modal[open]');
    if (openModal) {
      openModal.removeAttribute('open');
      document.body.style.overflow = '';
      releaseFocus(openModal.querySelector('.modal__content') || openModal);
    }
  });
})();
